<script setup lang="ts">
    import ModalBase from '@/shared/components/ModalBase.vue';
    import { Ref, ref } from 'vue';
    import { sqlQuery } from '@/shared/helpers/api.ts';
    import { http } from '@/shared/httpWrapper';

    type ProjectSource = 'LCIProject' | 'ProjectFacility' | 'Project';

    type Project = {
        Name: string;
        Description: string;
        Facility: string | null;
        DeliveryCode: string | null;
        Source?: ProjectSource;
    };

    type CreateProjectSettings = {
        Template: Project;
    };

    const props = defineProps({
        scope: {
            type: String,
            required: false,
            default: null
        }
    });

    const emit = defineEmits<{
        onCancel: [void];
        select: [Name: string];
        create: [CreateProjectSettings];
    }>();

    const loading: Ref<boolean> = ref(false);
    const projectCode: Ref<string> = ref('');
    const results: Ref<Project[] | null> = ref(null);
    const existing: Ref<Project | null> = ref(null);

    async function searchForProjects(project: string): Promise<void> {
        loading.value = true;
        results.value = null;
        existing.value = null;
        const result = (await sqlQuery(http, {
            query: `SELECT Name, Description, Scope AS Facility, DeliveryCode, 'LCIProject' AS Source
                      FROM LCIProject
                      WHERE Name = @projectCode
                    UNION
                    SELECT Name, Description, Facility, DeliveryCode, 'ProjectFacility' AS Source
                      FROM ProjectFacility
                      WHERE Name = @projectCode
                    UNION
                    SELECT Name, Description, NULL, NULL, 'Project' AS Source
                      FROM Project
                      WHERE Name = @projectCode`,
            arguments: {
                projectCode: project
            } })).data as Project[];
        existing.value = result.find(pr => pr.Source === 'LCIProject' && pr.Facility === props.scope) ?? null;
        if (!existing.value) {
            function compareBySource(a: Project, b: Project): number {
                const sources = ['LCIProject', 'ProjectFacility', 'Project'];
                if (a.Source === b.Source) {
                    return a.Facility?.localeCompare(b.Facility ?? '') ?? 1;
                }
                return sources.indexOf(a.Source ?? '') - sources.indexOf(b.Source ?? '');
            }

            results.value = [
                ...result.filter(pr => pr.Facility === props.scope).sort(compareBySource),
                ...result.filter(pr => pr.Facility !== props.scope).sort(compareBySource)
            ];
        }
        loading.value = false;
    }

    function templateFromName(name: string): Project {
        return {
            Name: name,
            Description: '',
            Facility: props.scope,
            DeliveryCode: null
        };
    }

    function onCancel(): void {
        emit('onCancel');
    }

</script>

<template>
    <modal-base @on-cancel="onCancel">
        <template #modal-header>
            Create project
        </template>
        <template #modal-body>
            The project you want to create may already exist, either associated to other facilities or in other project-related libraries.
            Enter the <b>project code</b> below to search for existing items:
            <div class="has-margin-top">
                <b-field
                    label="Project Code">
                    <b-input
                        v-model="projectCode"
                        expanded />
                    <b-button
                        label="Search"
                        icon-right="magnify"
                        type="is-primary"
                        :loading="loading"
                        @click="searchForProjects(projectCode)" />
                </b-field>
            </div>
            <div
                v-if="existing"
                class="has-margin-top">
                A project with this code already exists for this facility
                <div>
                    <b-button
                        type="is-primary"
                        class="has-margin-top"
                        icon-right="folder-open"
                        @click="emit('select', existing.Name)">
                        Go to existing project
                    </b-button>
                </div>
            </div>
            <div v-else-if="results !== null">
                <div
                    class="has-margin-top">
                    <h2 class="subtitle is-bold is-marginless">
                        <b>Select template:</b>
                    </h2>
                    <div
                        v-if="results.length > 0"
                        class="has-margin-top">
                        This project has existing items associated to other facilities or in other libraries. When creating a project
                        for this facility you may use one of these items as a template. If in doubt, you probably want to use the
                        top result. Alternatively, you may click the bottom button to create a project from scratch.
                        <b-table
                            class="has-margin-top"
                            :data="results || []">
                            <b-table-column
                                v-slot="table"
                                field="Description"
                                label="Description">
                                {{ table.row.Description }}
                            </b-table-column>
                            <b-table-column
                                v-slot="table"
                                field="DeliveryCode"
                                label="Delivery Code">
                                {{ table.row.DeliveryCode }}
                            </b-table-column>
                            <b-table-column
                                v-slot="table"
                                field="Facility"
                                label="Facility">
                                {{ table.row.Facility }}
                            </b-table-column>
                            <b-table-column
                                v-slot="table"
                                field="Source"
                                label="Source library">
                                {{ table.row.Source }}
                            </b-table-column>
                            <b-table-column
                                v-slot="table">
                                <b-button
                                    type="is-primary"
                                    @click="emit('create', { Template: table.row })">
                                    Use as template
                                </b-button>
                            </b-table-column>
                        </b-table>
                    </div>
                    <div
                        v-else
                        class="has-margin-top">
                        No existing projects found, to begin creating one use the button below.
                    </div>
                    <div
                        class="has-margin-top">
                        <b-field>
                            <b-button
                                type="is-primary"
                                icon-right="plus-circle-outline"
                                @click="emit('create', { Template: templateFromName(projectCode)})">
                                Create from scratch
                            </b-button>
                        </b-field>
                    </div>
                </div>
            </div>
        </template>
        <template #modal-footer>
            <b-button
                icon-right="close-circle"
                @click="onCancel">
                Cancel
            </b-button>
        </template>
    </modal-base>
</template>
